import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { SectionHeader, Reveal } from "@/components/SectionHeader";
import SEO from "@/components/SEO";

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Environmental Services — Mold, IAQ, Water Damage, Asbestos"
        description="Full independent environmental consulting practice: mold assessments, indoor air quality, moisture mapping, thermal imaging, water damage, remediation protocols, PRV, HVAC, asbestos."
        path="/services"
      />
      <section className="bg-[#F8FAFC] py-24 md:py-28">
        <div className="container-x">
          <div className="overline mb-5">Services</div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-[#0F172A] leading-[1.05] max-w-4xl">
            Comprehensive environmental consulting, from field diagnostics to independent clearance.
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Every service is delivered with the same scientific rigor, the same transparent
            reporting, and the same structural independence from remediation.
          </p>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.slug} delay={i * 0.03}>
                  <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="card-soft block p-8 h-full">
                    <div className="w-12 h-12 rounded-xl bg-[#7C3AED]/10 grid place-items-center mb-6">
                      <Icon className="w-6 h-6 text-[#7C3AED]" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-[#0F172A] mb-3">{s.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{s.summary}</p>
                    <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-[#7C3AED]">
                      Service details <ArrowRight className="w-4 h-4" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-[#0F172A] text-white">
        <div className="container-x">
          <SectionHeader
            overline="Our process"
            title="Four steps. Defensible at every stage."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Scope", d: "Define the question, the property, and the standards that apply." },
              { n: "02", t: "Field investigation", d: "Visual, moisture, thermal, and targeted sampling." },
              { n: "03", t: "Lab & interpretation", d: "AIHA-LAP analysis contextualized to the site." },
              { n: "04", t: "Protocol or PRV", d: "Written protocol, independent clearance, or both." },
            ].map((step) => (
              <div key={step.n} data-testid={`process-step-${step.n}`} className="rounded-2xl p-7 border border-white/10 bg-white/[0.03]">
                <div className="font-display font-extrabold text-3xl text-[#F59E0B]">{step.n}</div>
                <div className="font-display font-bold text-lg mt-4">{step.t}</div>
                <div className="text-sm text-white/60 mt-2 leading-relaxed">{step.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
